class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = Object.create(null);
    this.searchRequestId = 0;
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;
    this.removeAttribute('results');

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.searchRequestId++;
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const label = this.querySelector('[data-predictive-search-search-for-text]');
    if (!label || !previousTerm) return;
    const parts = label.textContent.split(previousTerm);
    if (parts.length !== 2) return;
    label.textContent = parts[0] + newTerm + parts[1];
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    if (!allVisibleElements.length) return;
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  normalizeSearchText(value) {
    return String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
      .replace(/[^\p{L}\p{M}\p{N}]+/gu, ' ').trim().replace(/\s+/g, ' ');
  }

  isOneSearchEditApart(left, right) {
    if (Math.abs(left.length - right.length) > 1) return false;
    let a = 0, b = 0, edits = 0;
    while (a < left.length && b < right.length) {
      if (left[a] === right[b]) { a++; b++; continue; }
      if (++edits > 1) return false;
      if (left.length <= right.length) b++;
      if (left.length >= right.length) a++;
    }
    return edits + (a < left.length || b < right.length ? 1 : 0) <= 1;
  }

  searchTitleScore(title, query) {
    const name = this.normalizeSearchText(title), terms = this.normalizeSearchText(query).split(' ').filter(Boolean);
    if (!name || !terms.length) return 0;
    const words = name.split(' ');
    let score = 0;
    for (const term of terms) {
      const match = Math.max(0, ...words.map(word => word === term ? 12 : word.startsWith(term) ? 10 :
        term.length >= 4 && word.includes(term) ? 7 : term.length >= 4 && this.isOneSearchEditApart(term, word) ? 4 : 0));
      if (!match) return 0;
      score += match;
    }
    if (name === terms.join(' ')) score += 100;
    else if (name.startsWith(terms.join(' '))) score += 30;
    return score;
  }

  prioritizeSearchProducts(root, query) {
    root.querySelector('#predictive-search-products')?.classList.add('visually-hidden');
    root.querySelector('#predictive-search-option-search-keywords')?.remove();
    // Rank name matches first without discarding Shopify's other relevant matches.
    if (this.normalizeSearchText(query).length < 2) return;
    const list = root.querySelector('#predictive-search-results-products-list');
    if (!list) return;
    const products = Array.from(list.children).map((item, index) => ({item, index,
      score: this.searchTitleScore(item.querySelector('.predictive-search__item-heading')?.textContent, query)}));
    products.sort((a, b) => b.score - a.score || a.index - b.index)
      .forEach(product => list.append(product.item));
  }

  async getSearchResults(searchTerm) {
    const query = searchTerm.trim().replace(/\s+/g, ' ');
    const queryKey = query.toLowerCase();
    this.abortController.abort();
    this.abortController = new AbortController();
    const requestId = ++this.searchRequestId;
    const signal = this.abortController.signal;
    const isCurrent = () => requestId === this.searchRequestId && queryKey === this.getQuery().trim().replace(/\s+/g, ' ').toLowerCase();
    this.setLiveRegionLoadingState();
    const searchDeferred = this.dispatchSearchUpdateEvent(query);
    try {
      let resultsMarkup = this.cachedResults[queryKey];
      if (!resultsMarkup) {
        const params = new URLSearchParams({q: query, section_id: 'predictive-search', 'resources[type]': 'product',
          'resources[limit]': '10', 'resources[limit_scope]': 'each', 'resources[options][unavailable_products]': 'last'});
        const response = await fetch(routes.predictive_search_url + '?' + params, {signal});
        if (!response.ok) throw new Error('Search suggestions could not be loaded (' + response.status + ').');
        const text = await response.text();
        if (!isCurrent()) throw new DOMException('Superseded search', 'AbortError');
        const root = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        if (!root) throw new Error('Search suggestions were unavailable.');
        this.prioritizeSearchProducts(root, query);
        const count = root.querySelector('[data-total-results]');
        if (count && Number(count.dataset.totalResults) === 0) {
          const empty = document.createElement('p');
          empty.className = 'predictive-search__item caption-large';
          empty.style.margin = '0';
          empty.textContent = count.textContent.trim().replace(/\s+/g, ' ');
          const searchAll = root.querySelector('[data-predictive-search-search-for-text]')?.closest('[role="option"]');
          if (searchAll) searchAll.before(empty);
          else (root.querySelector('#predictive-search-results') || root).prepend(empty);
        }
        resultsMarkup = root.innerHTML;
        this.allPredictiveSearchInstances.forEach(instance => {
          instance.cachedResults[queryKey] = resultsMarkup;
          const keys = Object.keys(instance.cachedResults);
          if (keys.length > 40) delete instance.cachedResults[keys[0]];
        });
      }
      if (!isCurrent()) throw new DOMException('Superseded search', 'AbortError');
      this.renderSearchResults(resultsMarkup);
      searchDeferred?.resolve({totalCount: this.getTotalResultCount()});
    } catch (error) {
      searchDeferred?.reject(error);
      if (error?.name === 'AbortError' || error?.code === 20 || !isCurrent()) return;
      this.close();
      console.warn('Predictive search:', error);
    }
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.searchRequestId++;
    this.abortController.abort();
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
